import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Offcanvas } from 'react-bootstrap'
import Select from 'react-select'

import { GET, POST } from 'helpers/api'
import { formatMoneyInput } from 'helpers/helpers'

import { PAYMENT_TYPES, CURRENCIES, currencyLabel, formatAmount, parseAmountInput } from './financeUtils'
import AccountSelect from './AccountSelect'

function newPayment(paymentType) {
	return { paymentType, amount: '' }
}

function getInitialForm() {
	return {
		expenseId: null,
		accountKey: null,
		currencyId: CURRENCIES[0].id,
		payments: [newPayment(PAYMENT_TYPES[0].key)],
		note: '',
	}
}

function CreateExpense({ show, onHide, onSaved }) {
	const { t } = useTranslation()

	const [form, setForm] = useState(getInitialForm)
	const [expenseTypes, setExpenseTypes] = useState([])
	const [accounts, setAccounts] = useState([])
	const [typesLoading, setTypesLoading] = useState(false)
	const [accountsLoading, setAccountsLoading] = useState(false)
	const [saving, setSaving] = useState(false)

	const paymentTypeOptions = PAYMENT_TYPES.map(item => ({ value: item.key, label: t(item.i18n) }))
	const account = accounts.find(item => item.key === form.accountKey)
	const currency = currencyLabel(form.currencyId)
	const total = form.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)
	const loading = typesLoading || accountsLoading
	const error = loading ? null : getError()

	function getError() {
		if (!form.expenseId) return t('FO_SELECT_TYPE')
		if (!account) return t('FO_SELECT_ACCOUNT')
		if (form.payments.some(payment => !(Number(payment.amount) > 0))) return t('FO_FILL_AMOUNT')
		return null
	}

	async function getExpenseTypes() {
		setTypesLoading(true)
		try {
			const response = await GET('/services/web/api/expense-helper')
			const types = Array.isArray(response) ? response : []
			setExpenseTypes(types)
			setForm(prev => ({
				...prev,
				expenseId: types.some(item => item.id === prev.expenseId) ? prev.expenseId : null,
			}))
		} finally {
			setTypesLoading(false)
		}
	}

	// cashboxes with an open shift (sourceType CASHBOX) and POS safes (SAFE) with their cash/terminal balance
	// in the selected currency; isActive() is false when the currency changed again or the panel was closed
	async function getAccounts(currencyId, isActive) {
		setAccountsLoading(true)
		try {
			const [cashboxes, safes] = await Promise.all([
				GET('/services/web/api/expense/open-cashbox-with-balance', { currencyId }),
				GET('/services/web/api/expense/safe-with-balance', { currencyId }),
			])
			if (!isActive()) return
			const list = [
				...(Array.isArray(cashboxes) ? cashboxes : []).map(item => ({ ...item, sourceType: 'CASHBOX', key: 'CASHBOX-' + item.cashboxId })),
				...(Array.isArray(safes) ? safes : []).map(item => ({ ...item, sourceType: 'SAFE', key: 'SAFE-' + item.posId, posName: item.name })),
			]
			setAccounts(list)
			setForm(prev => ({
				...prev,
				accountKey: list.some(item => item.key === prev.accountKey) ? prev.accountKey : null,
			}))
		} finally {
			if (isActive()) {
				setAccountsLoading(false)
			}
		}
	}

	// UZS -> $ -> UZS
	function switchCurrency() {
		const index = CURRENCIES.findIndex(item => item.id === form.currencyId)
		setForm({ ...form, currencyId: CURRENCIES[(index + 1) % CURRENCIES.length].id })
	}

	function changePayment(index, key, value) {
		setForm({ ...form, payments: form.payments.map((payment, i) => i === index ? { ...payment, [key]: value } : payment) })
	}

	function addPayment() {
		const unused = PAYMENT_TYPES.find(item => !form.payments.some(payment => payment.paymentType === item.key))
		setForm({ ...form, payments: [...form.payments, newPayment(unused.key)] })
	}

	function removePayment(index) {
		setForm({ ...form, payments: form.payments.filter((payment, i) => i !== index) })
	}

	// CASHBOX needs cashboxId + shiftId, SAFE needs posId; the backend ignores the others
	async function save() {
		const request = {
			expenseId: form.expenseId,
			sourceType: account.sourceType,
			cashboxId: account.cashboxId ?? null,
			shiftId: account.shiftId ?? null,
			posId: account.posId ?? null,
			currencyId: form.currencyId,
			cashAmount: 0,
			terminalAmount: 0,
			paymentPurposeId: null,
			note: form.note,
		}
		form.payments.forEach(payment => {
			request[payment.paymentType] = Number(payment.amount)
		})

		setSaving(true)
		try {
			const response = await POST('/services/web/api/expense-create', request, { loader: true, message: true })
			if (response) {
				onSaved()
			}
		} finally {
			setSaving(false)
		}
	}

	useEffect(() => {
		if (show) {
			getExpenseTypes()
		}
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!show) return
		let active = true
		getAccounts(form.currencyId, () => active)
		return () => { active = false }
	}, [show, form.currencyId]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Offcanvas show={show} placement="end" className="fo-offcanvas" backdrop="static"
			onHide={onHide} onExited={() => setForm(getInitialForm())}>
			<Offcanvas.Header closeButton>
				<Offcanvas.Title>{t('FO_NEW_EXPENSE')}</Offcanvas.Title>
			</Offcanvas.Header>
			<Offcanvas.Body>
				<div className="form-group">
					<label>{t('OPERATION_TYPE')}</label>
					<div className="fo-operation-type active">
						<span className="fo-operation-type-icon">
							<i className="uil uil-receipt"></i>
						</span>
						{t('FO_OTHER_EXPENSE')}
						<input type="radio" className="form-check-input" tabIndex="-1" checked readOnly />
					</div>
				</div>

				<div className="form-group">
					<label>{t('TYPE')}</label>
					<Select
						options={expenseTypes}
						value={expenseTypes.find(option => option.id === form.expenseId) || null}
						onChange={(option) => setForm({ ...form, expenseId: option.id })}
						isLoading={typesLoading}
						placeholder=""
						noOptionsMessage={() => t('LIST_IS_EMPTY')}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
					/>
				</div>

				<div className="form-group">
					<label>{t('ACCOUNT')}</label>
					<AccountSelect
						options={accounts}
						value={account}
						onChange={(option) => setForm({ ...form, accountKey: option.key })}
						isLoading={accountsLoading}
						emptyMessage="LIST_IS_EMPTY"
					/>
					{account && !accountsLoading &&
						<div className="text-muted text-sm mt-1">
							{PAYMENT_TYPES
								.map(item => `${t(item.i18n)} ${formatAmount(account[item.key], currencyLabel(account.currencyId, account.currencyName))}`)
								.join(' · ')}
						</div>
					}
				</div>

				<div className="form-group">
					<label>{t('AMOUNT')}</label>
					{form.payments.map((payment, index) => (
						<div key={payment.paymentType} className="fo-payment-row d-flex gap-2 mb-2">
							<div className="fo-payment-type">
								<Select
									options={paymentTypeOptions.filter(option =>
										option.value === payment.paymentType || !form.payments.some(item => item.paymentType === option.value)
									)}
									value={paymentTypeOptions.find(option => option.value === payment.paymentType)}
									onChange={(option) => changePayment(index, 'paymentType', option.value)}
									isSearchable={false}
								/>
							</div>
							<div className="input-group">
								<input type="text" inputMode="decimal" className="form-control" placeholder="0"
									value={formatMoneyInput(payment.amount)}
									onChange={(e) => changePayment(index, 'amount', parseAmountInput(e.target.value))} />
								{/* one currency for the whole expense: switched by a tap in the first row */}
								{index === 0 ?
									<button type="button" className="input-group-text fo-currency fo-currency-switch" title={t('CURRENCY')} onClick={switchCurrency}>
										{currency}
										<svg width="8" height="12" viewBox="0 0 8 12" aria-hidden="true">
											<path d="M1.5 4.5 4 2l2.5 2.5M1.5 7.5 4 10l2.5-2.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</button>
									:
									<span className="input-group-text fo-currency">{currency}</span>
								}
							</div>
							{form.payments.length > 1 &&
								<button type="button" className="btn btn-outline-danger" title={t('DELETE')} onClick={() => removePayment(index)}>
									<i className="uil uil-trash-alt"></i>
								</button>
							}
						</div>
					))}
					<div className="d-flex align-items-center">
						{form.payments.length < PAYMENT_TYPES.length &&
							<button type="button" className="btn btn-link text-primary text-decoration-none p-0" onClick={addPayment}>
								<i className="uil uil-plus me-1"></i>
								{t('ADD')}
							</button>
						}
						<div className="ms-auto">
							<b>{t('GRAND_TOTAL')}:</b> {formatAmount(total, currency)}
						</div>
					</div>
				</div>

				<div className="form-group">
					<label>{t('COMMENT')}</label>
					<textarea className="form-control" rows="3" placeholder={t('NOT_NECESSARY')}
						value={form.note}
						onChange={(e) => setForm({ ...form, note: e.target.value })} />
				</div>
			</Offcanvas.Body>

			<div className="fo-offcanvas-footer">
			
				<button type="button" className="btn btn-outline-warning ms-auto" onClick={onHide}>{t('CANCEL')}</button>
				<button type="button" className="btn btn-primary" disabled={loading || saving || !!error} onClick={save}>{t('SAVE')}</button>
			</div>
		</Offcanvas>
	)
}

export default CreateExpense
